/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("RadioGroupWidget", ["FieldWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * RadioGroup widget.
   * @class RadioGroupWidget
   * @memberOf classes
   * @extends classes.FieldWidgetBase
   * @publicdoc Widgets
   */
  cls.RadioGroupWidget = context.oo.Class(cls.FieldWidgetBase, function ($super) {
    return /** @lends classes.RadioGroupWidget.prototype */ {
      __name: "RadioGroupWidget",
      /**
       * currently aimed item
       * @type {number}
       */
      _currentlyAimedIndex: 0,

      /**
       * Widget value
       * @type {string}
       */
      _value: "",
      /**
       * @type {boolean}
       */
      _notNull: false,
      /**
       * @type {boolean}
       */
      _allowNullValue: false,

      /**
       * @type {HTMLElement[]}
       */
      _itemElements: null,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
        this.setVMFocusable(true);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this._ignoreLayout) {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutEngine = new cls.LeafDynamicHeightLayoutEngine(this);
          this._layoutInformation.getSizePolicyConfig().initial = cls.SizePolicy.Dynamic();

          this._layoutInformation.getSizePolicyConfig().setAllShrinkable(false);
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        $super.destroy.call(this);
        this._itemElements = null;
      },

      /**
       * Manage navigation keys
       * @param {string} keyString - key string representation
       * @returns {boolean} returns if the key has been processed
       * @private
       */
      _manageNavigationKey: function (keyString) {
        if (this.isInArray()) {
          return false;
        }

        let keyProcessed = true;
        switch (keyString) {
          case "down":
            this._onNext();
            break;
          case "up":
            this._onPrevious();
            break;
          default:
            keyProcessed = false;
        }

        return keyProcessed;
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        if (this.isEnabled() && !this.isReadOnly()) {
          keyProcessed = this._manageNavigationKey(keyString);
          if (keyProcessed) {
            return true;
          }

          keyProcessed = true;
          switch (keyString) {
            case this.getEnd():
              this._onNext();
              break;
            case this.getStart():
              this._onPrevious();
              break;
            case "space":
              this._onSpace();
              break;
            default:
              keyProcessed = false;
          }
        }

        if (keyProcessed) {
          return true;
        }
        return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onRequestFocus(domEvent);
        if (domEvent.target.closest(`.${this.getBEMPartName("item")}`)) {
          this._onItemClick(domEvent);
        }
        this.emit(context.constants.widgetEvents.click, domEvent);

        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * On click handler
       * @param {Object} evt - DOM event
       * @private
       */
      _onItemClick: function (evt) {
        if (!this.isEnabled()) {
          return;
        }

        const clickedIndex = this._indexOf(evt.target.closest(`.${this.getBEMPartName("item")}`).dataset.value ?? null);
        if (clickedIndex === -1) {
          return;
        }
        this._currentlyAimedIndex = clickedIndex;

        let newValue = this._getIndexValue(this._currentlyAimedIndex, false);
        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * On previous handler : action to do when we want to go to previous value
       * @private
       */
      _onPrevious: function () {
        this._currentlyAimedIndex -= 1;
        if (this._currentlyAimedIndex < 0) {
          this._currentlyAimedIndex = this.getItemElements().length - 1;
        }

        const newValue = this._getIndexValue(this._currentlyAimedIndex, true);
        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * On next handler : action to do when we want to go to next value
       * @private
       */
      _onNext: function () {
        this._currentlyAimedIndex += 1;
        if (this._currentlyAimedIndex >= this.getItemElements().length) {
          this._currentlyAimedIndex = 0;
        }

        const newValue = this._getIndexValue(this._currentlyAimedIndex, true);
        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * On space Key handler
       * @private
       */
      _onSpace: function () {
        const newValue = this._getIndexValue(this._currentlyAimedIndex, false);
        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * Return the index of the item having the value in parameter
       * @param {string} value The value of the item to find
       * @returns The index of the item that have the value. -1 if not found
       * @private
       */
      _indexOf: function (value) {
        return this.getItemElements().findIndex((element) => (element.dataset.value ?? null) === value);
      },

      /**
       * Add a choice to the list
       * @param {object} choice - choice to add
       * @private
       */
      _addChoice: function (choice) {
        const item = context.TemplateService.renderDOM("RadioGroupItem");
        item.dataset.value = choice.value;
        item.getElementsByClassName(this.getBEMPartName("itemText"))[0].textContent = choice.text;
        item.addEventListener("focus", () => {
          const index = this.getItemElements().indexOf(item);
          if (index !== -1) {
            this._currentlyAimedIndex = index;
            this._updateVisual(this.getValue(), this._currentlyAimedIndex);
          }
        });
        this._element.appendChild(item);
        this._itemElements = null;
        if (this.getLayoutEngine()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * @deprecated
       * Removes a choice at the given index
       * @param {number} index
       * @private
       */
      _removeChoiceAt: function (index) {
        this._element.getElementsByClassName(this.getBEMPartName("item"))[index].remove();
        this._itemElements = null;
      },

      /**
       * @deprecated
       * Removes the given choice
       * @param {object} choice
       * @private
       */
      _removeChoice: function (choice) {
        const index = this._indexOf(choice.value);
        if (index >= 0) {
          this._removeChoiceAt(index);
        }
        if (this.getLayoutEngine()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * Adds a single or a list of choices
       * @param {string|string[]} choices - choices to add to the radio ensemble
       * @publicdoc
       */
      setItems: function (choices) {
        this.clearChoices();
        if (!choices) {
          return;
        }
        if (Array.isArray(choices)) {
          for (const element of choices) {
            this._addChoice(element);
          }
          this.updateValue();
        } else {
          this._addChoice(choices);
        }
        this._addAriaNavigation();
      },

      // TODO : Remove
      /**
       * @deprecated use clear then set
       * Removes a single or a list of choices
       * @param {(string|string[])} choices - choices to remove
       * @publicdoc
       */
      removeChoices: function (choices) {
        if (choices) {
          if (Array.isArray(choices)) {
            for (const element of choices) {
              this._removeChoice(element);
            }
          } else {
            this._removeChoice(choices);
          }
        }
      },

      /**
       * Clears all choices
       * @publicdoc
       */
      clearChoices: function () {
        for (const item of this.getItemElements()) {
          item.remove();
        }
        this._itemElements = null;

        if (this.getLayoutEngine()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * Set the layout orientation.
       * @param {string} orientation - 'vertical' or 'horizontal' or ''.
       * @publicdoc
       */
      setOrientation: function (orientation) {
        this.toggleClasses(orientation === "horizontal", "horizontal");
        if (this.getLayoutEngine()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * Get the layout orientation.
       * @returns {string} the layout orientation. 'vertical' or 'horizontal'.
       * @publicdoc
       */
      getOrientation: function () {
        if (this._element.classList.contains("gbc_RadioGroupWidget_horizontal")) {
          return "horizontal";
        }
        return "vertical";
      },

      /**
       * Get the value of the radiogroup
       * @returns {string} value - the current value
       * @publicdoc
       */
      getValue: function () {
        return this._value;
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM) {
        $super.setValue.call(this, value, fromVM);
        this._setValue(value);
      },

      /**
       * Internal setValue used to be inherited correctly by DummyWidget
       * @param {string} value - the value to set
       * @private
       */
      _setValue: function (value) {
        this._value = value;
        const indexOfValue = this._indexOf(value);
        this._currentlyAimedIndex = indexOfValue !== -1 ? indexOfValue : this._currentlyAimedIndex;
        this._updateVisual(value, this._currentlyAimedIndex);
      },

      /**
       * Set again the value, can be useful if items have changed
       * @publicdoc
       */
      updateValue: function () {
        if (this._value) {
          this.setValue(this._value);
        }
      },

      /**
       * Update the UI, with the value for the given index.
       * NOT NULL wise
       * !! This method don't change the value of the widget class !!
       * @param {number} index - index of the value
       * @param {boolean} allowKeepNull If true, the value will stay null if it was null before (used for previous and next)
       * @returns {string} The value corresponding to the visual
       * @private
       */
      _getIndexValue: function (index, allowKeepNull) {
        if (!this.isEnabled()) {
          return this.getValue();
        }
        const childItem = this.getItemElements()[index];

        if (childItem === undefined) {
          return this.getValue();
        }

        const wantedValue = childItem.dataset.value ?? null;
        let newValue = this.getValue() === "" && allowKeepNull ? "" : wantedValue;

        if (wantedValue === this.getValue() && !this._notNull) {
          newValue = "";
        }

        // Return the value
        return newValue;
      },

      /**
       * Returns the text corresponding to a value
       * @param {String} value
       */
      getTextForValue: function (value) {
        for (const item of this.getItemElements()) {
          const itemValue = item.dataset.value ?? null;
          if (itemValue === value) {
            return item.getElementsByClassName(this.getBEMPartName("itemText"))[0].textContent;
          }
        }
        return "";
      },

      /**
       * Manage RadioGroup visual depending on the values given in parameters
       * @param {string} checkedValue The checked value, used to find the item to check. If null, no item will be checked
       * @param {number|undefined} aimedIndex The index of the aimed item, if set to undefined, it will be the same as the checkedValue item.
       */
      _updateVisual: function (checkedValue, aimedIndex) {
        const isEnabled = this.isEnabled();
        this.getItemElements().forEach((item, index) => {
          const itemValue = item.dataset.value ?? null;
          // Foreach children, toggle 'checked', and 'unchecked' classes
          const boxElement = item.getElementsByClassName(this.getBEMPartName("itemIcon"))[0];
          cls.DOMElementHelper.toggleClasses(
            boxElement,
            itemValue === checkedValue,
            context.constants.widgetCssClasses.checked,
            context.constants.widgetCssClasses.unchecked
          );
          const itemIsSelected = index === aimedIndex;
          cls.DOMElementHelper.toggleClasses(item, itemIsSelected, context.constants.widgetCssClasses.isSelected);
          item.setAttribute("aria-checked", String(itemValue === checkedValue));
          // Roving tabindex: only the aimed item receives Tab focus, others are reached via arrow keys.
          // When disabled (DISPLAY / DisplayArray / NOENTRY / setFieldActive(false)) every item is removed
          // from the Tab order
          item.setAttribute("tabindex", isEnabled && itemIsSelected ? "0" : "-1");
        });
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled) {
        const prevEnabled = this._enabled;
        $super.setEnabled.call(this, enabled);
        if (prevEnabled === this._enabled) {
          return;
        }
        this.toggleClass("disabled", !enabled);
        // Re-run the roving update so item tabindexes reflect the new enabled state:
        // disable → every item tabindex="-1" ; enable → restore tabindex="0" on the aimed item.
        this._updateVisual(this.getValue(), this._currentlyAimedIndex);
      },

      /**
       * @inheritDoc
       */
      domFocus: function (preventScroll = false) {
        // Focus the currently aimed item (tabindex="0") so the SR announces it directly.
        const aimedItem = this.getItemElements()[this._currentlyAimedIndex];
        cls.DOMElementHelper.domFocus(aimedItem || this._element, preventScroll);
      },

      setFocus: function (fromMouse = false, preventScroll = false) {
        this.domFocus(preventScroll);
        this._updateVisual(this.getValue(), this._currentlyAimedIndex);
        $super.setFocus.call(this, fromMouse, preventScroll);
      },

      /**
       * Set widget mode. Useful when widget have peculiar behavior in certain mode
       * @param {string} mode the widget mode
       * @param {boolean} active the active state
       */
      setWidgetMode: function (mode, active) {
        this._allowNullValue = mode === "Construct";
      },

      /**
       * Add accessible navigation for radiogroup
       * @private
       */
      _addAriaNavigation: function () {
        this.getItemElements().forEach((item, index, array) => {
          item.id = `w_${this.getUniqueIdentifier()}_r_${index}`;
          item.setAttribute("aria-posinset", String(index + 1));
          item.setAttribute("aria-setsize", String(array.length));
        });
      },

      /**
       * Get the items HTML elements. And cache them
       * @returns {HTMLElement[]}
       */
      getItemElements() {
        if (!this._itemElements) {
          this._itemElements = Array.from(this.getElement().getElementsByClassName(this.getBEMPartName("item")));
        }
        return this._itemElements;
      },

      /**
       * @inheritDoc
       */
      getValueForClipboard: function (ignoreSelection = false) {
        let valueForCB = null;
        if (!ignoreSelection) {
          valueForCB = cls.WindowHelper.getSelectionText();
          valueForCB = valueForCB.length > 0 ? valueForCB : null;
        }
        if (valueForCB === null) {
          valueForCB = this.getTextForValue(this.getValue());
        }
        return valueForCB;
      },

      /**
       * @inheritDoc
       */
      getContextMenuAuthorizedActions: function () {
        return {
          paste: false,
          copy: true,
          cut: false,
          selectAll: false
        };
      }
    };
  });
  cls.WidgetFactory.registerBuilder("RadioGroup", cls.RadioGroupWidget);
});
